import React, { useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import DropDownMenuProfile from '../../../components/DropDown/DropDownMenuProfile';
import DropDownMenuItem from '../../../components/DropDown/DropDownMenuItem';
import {
    DeleteIconsOptions,
    UpdateIconsOptions,
    IconChipText, IconClip,
    IconUserAuthority, IconChipDocument,
} from '../../../components/buttons/IconButtons';
import CustomGenericTittleIconDataChip from '../../../components/CustomGenericTittleIconDataChip';
import {FIELD_TYPE, FIELD_TYPES_MODEL} from '../../Dashboard/Visitor/Type/Model/Model';
import CustomUserChip from '../../../components/CustomUserChip';
import CustomChip from '../../../components/CustomChip';
import {colors} from '../../../enums/colors';
import TableFunction from '../../../components/table/TableFunction';
import routes from '../../../Routes/routes';
import {downloadActionFile, EMPTY_VALUES, errorMsg, sanitizeFileName} from '../../../Apis/functions';
import {VIEW_TYPE} from "../../../enums/ViewType";
import {LEFT_FIRST_COLUMN_WITH_CHECKBOX, SEARCH_MODEL} from "../../../components/table/Model/TableModel";
import {SimpleSearch} from "../../../reduxToolkit/Search/SearchApi";
import {profileOptions} from "../Model/Model";
import {
    DeleteDocumentationApprovalFlowByIds,
    GetDocumentApprovalFlowById
} from "../../../reduxToolkit/DocumentationApproval/DocumentationApprovalApi";
import {USER_TYPE_MODEL} from "../../../enums/userTypeEnum";
import {toast} from "react-toastify";
import ButtonSubAction from "../../../components/buttons/ButtonSubAction";
import {permissionObj} from "../../../Helpers/permission";
import { URL } from '../../../Apis/Constants';

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 */

/**
 * ApprovalFlowSubViewApp
 *
 * Handles the listing, searching, pagination, expansion, deletion, and details rendering
 * of documentation approval flows for Contractors and Suppliers.
 *
 * Provides:
 *  - Search and pagination handlers
 *  - Table column configuration
 *  - Row expansion to fetch levels
 *  - Delete handlers
 *  - Menu options (Update/Delete)
 *  - Rendering of approval flow levels and metadata
 *
 * @param {string} searchElement - Search keyword entered in the input field.
 * @param {Function} setIsAllChecked - Setter to update "select all" checkbox state.
 * @param {Array<string|number>} selectedList - List of selected row IDs.
 * @param {Function} setSelectedList - Setter for managing selected rows.
 * @param {Object} option - Optional filters for searching approval flows.
 * @param {Function} setDeleteApprovalFlowShow - Setter to toggle delete confirmation modal visibility.
 * @param {string} viewType - View mode for the search request (e.g., LIST, GRID).
 * @param {Array<number>} userTypeIds - User type identifiers used for permission-based routing.
 *
 */
const ApprovalFlowSubViewApp = ({
                                    searchElement,
                                    setIsAllChecked,
                                    selectedList,
                                    setSelectedList,
                                    option,
                                    setDeleteApprovalFlowShow,
                                    viewType,
                                    userTypeId
                                }) => {

    const dispatch = useDispatch();
    const [searchResults, setSearchResults] = useState([]);
    const [levels, setLevels] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [modified, setModified] = useState(false);
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const searchId = 'txt_afs_request'; // Search input ID for DOM usage

    /**
     * Delete selected approval flow(s).
     *
     * Accepts:
     *  - array of IDs
     *  - single ID string
     *  - object where values are IDs
     *
     * Dispatches API call to delete documentation approval flows.
     *
     * @function handleDelete
     * @param {Array|string|Object} item - IDs or structure containing IDs.
     * @returns {void}
     */
    const handleDelete = (item) => {
        let ids = [];
        if (Array.isArray(item)) {
            ids = item;
        } else if (typeof item === 'string') {
            ids = [item];
        } else if (item && typeof item === 'object') {
            ids = Object.values(item);
        } else return;

        dispatch(DeleteDocumentationApprovalFlowByIds(ids))
            .then(({payload: {data: {success, code}}}) => {
                if (success) {
                    toast.success(t('has_been_deleted_successfully'));
                    setSelectedList([]);
                    setModified(true);
                    setIsAllChecked(false);
                } else errorMsg(code);
            });
    };


    /**
     * Renders dropdown menu for each table row.
     *
     * Includes Update and Delete options depending on user permissions.
     *
     * @function renderOptionsMenu
     * @param {Object} row - Table row data.
     * @param {number} index - Row index.
     * @returns {JSX.Element}
     */
    const renderOptionsMenu = (row, index) => {
        return (
            <>
                <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${row.id}`}>
                    { (DEV || permission?.includes(permissionObj?.WEB_CONTRACTOR_APPROVAL_FLOW_UPDATE)
                           || permission?.includes(permissionObj?.WEB_SUPPLIER_APPROVAL_FLOW_UPDATE)) &&
                        <DropDownMenuItem
                            id={'btn_afsv_update_' + row.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('update')}
                            icon={<UpdateIconsOptions className='icon-color-general-950'/>}
                            click={() => {
                                if(userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE)navigate(`${routes.contractor.updateApprovalLevel}${row.id}`);
                                else if(userTypeId === USER_TYPE_MODEL.SUPPLIER_IN_CHARGE)navigate(`${routes.suppliers.updateApprovalLevel}${row.id}`);
                            }}
                            fontColor='font-color-shades-950'
                        />
                    }
                    {
                        (DEV || permission?.includes(permissionObj?.WEB_CONTRACTOR_APPROVAL_FLOW_DELETE)
                             || permission?.includes(permissionObj?.WEB_SUPPLIER_APPROVAL_FLOW_DELETE)) &&
                        <DropDownMenuItem
                            id={'btn_afsv_delete_' + row.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('delete')}
                            fontColor='font-color-error-600'
                            icon={<DeleteIconsOptions className='icon-color-error-code-600'/>}
                            click={() => {
                                setSelectedList([row.id]);
                                setDeleteApprovalFlowShow(true);
                            }}
                        />
                    }

                </DropDownMenuProfile>
            </>
        );
    };

    /**
     * Component for rendering expanded row details.
     *
     * Displays approval level timeline, department, documentation type, and file reference.
     *
     * @component RenderDetails
     * @param {Object} props
     * @param {Object} props.row - Row for which details are displayed.
     * @returns {JSX.Element}
     */
    const RenderDetails = ({row}) => {
        return (
            <>
                {levels.length > 0 && (
                    <>
                        <div className="flex-column w-100 mb-4">
                            <CustomGenericTittleIconDataChip title={'approval_level_documentation'}
                                                             Icon={IconUserAuthority}/>
                        </div>

                        {[...levels]
                            .sort((a, b) => a.levelNumber - b.levelNumber)
                            .map((level, index) => {
                                const fieldType = FIELD_TYPES_MODEL().find((f) => f.id === level?.type);
                                const isLast = index === levels.length - 1;

                                return (
                                    <div key={level.id} className="pl-32px position-relative">
                                        <div className="timeline-dot mt-4"/>
                                        {!isLast && <div className="timeline-line mt-4"/>}
                                        <div className="row my-1 align-items-center">
                                            <div className="d-flex justify-content-start align-items-center gap-4 align-items-center my-1 px-0">
                                                <div className="fs-14px font-color-general-950 ibl-font-w-600 px-0">
                                                    {t('level')} {level.levelNumber}
                                                </div>
                                                <CustomUserChip
                                                    className={"mt-0"}
                                                    key={level.id}
                                                    name={level.name}
                                                    showAvatar={true}
                                                    Icon={fieldType.id === FIELD_TYPE.TEXT.name ? IconChipText : IconChipDocument}
                                                    classNameIcon="icon-color-shades-400"
                                                />
                                            </div>

                                            <div className="border-bottom-shades-300 mt-3 mb-3"/>

                                            <div
                                                className="mt-3 ml-4 d-flex justify-content-start align-items-center gap-2">
                                                <p className="fs-14px font-color-general-950 ibl-font-w-400 w-200px">
                                                    {t('validation_department')}
                                                </p>
                                                <CustomChip
                                                    background={colors.secondary100}
                                                    fontColor={colors.secondary700}
                                                    borderColor={colors.secondary150}
                                                    legend={t(level?.department.name)}
                                                />
                                            </div>

                                            <div
                                                className="mt-3 ml-4 d-flex justify-content-start align-items-center gap-2">
                                                <p className="fs-14px font-color-general-950 ibl-font-w-400 w-200px">
                                                    {t('type_of_documentation')}
                                                </p>
                                                <CustomChip
                                                    background={colors.secondary100}
                                                    fontColor={colors.secondary700}
                                                    borderColor={colors.secondary150}
                                                    legend={t(fieldType?.name || 'unknown')}
                                                    icon={fieldType?.icon}
                                                    className="icon-color-secondary-700"
                                                />
                                            </div>

                                            <div
                                                className="mt-3 ml-4 d-flex justify-content-start align-items-center gap-2">
                                                <p className="fs-14px font-color-general-950 ibl-font-w-400 w-200px">
                                                    {t('reference_documentation')}
                                                </p>
                                                {level?.fileUploadDto !== null ?
                                                    <ButtonSubAction
                                                        name={sanitizeFileName(level?.fileUploadDto?.userFileNames[0])}
                                                        Icon={IconClip}
                                                        iconProps={'icon-color-secondary-700'}
                                                        className={'font-color-secondary-700 m-0 p-0'}
                                                        nameClass={'fs-12px m-0 p-0'}
                                                        onClick={() => {
                                                            downloadActionFile(URL + level?.fileUploadDto?.filePath);
                                                        }}
                                                    /> : EMPTY_VALUES.DEFAULT
                                                }
                                            </div>

                                            <div className="border-bottom-shades-300 mt-3 mb-3"/>
                                        </div>
                                    </div>
                                );
                            })}
                    </>
                )}
            </>
        )
    };

    /**
     * Expands selected row and fetches approval flow levels.
     *
     * Calls API: GetDocumentApprovalFlowById
     *
     * @function handleRowExpand
     * @param {Object} row - Row object containing `id`.
     * @returns {void}
     */
    const handleRowExpand = (row) => {
        if (row?.id) {
            dispatch(GetDocumentApprovalFlowById(row?.id))
                .then(({payload: {data: {data, success, code}}}) => {
                    if (success) {
                        setLevels(Array.isArray(data?.levels) ? data.levels : []);
                    } else errorMsg(code)
                })
            setExpandedRowId(row?.id);
        }
    };


    /**
     * Table column configuration.
     *
     * @constant
     * @type {Array<Object>}
     */
    const columns = [
        {
            field: 'serviceType.name',
            label: 'service_type',
            className: 'w-100',
            fixed: true,
            left: LEFT_FIRST_COLUMN_WITH_CHECKBOX
        },
        {
            field: 'profile',
            label: 'profile',
            render: (value) => {
                const option = profileOptions.find((opt) => opt.id === value);
                return (
                    <CustomChip
                        background={colors.secondary100}
                        fontColor={colors.secondary700}
                        borderColor={colors.secondary150}
                        legend={t(option?.name)}
                    />
                );
            },
        },
    ];

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({
            page, size: rowsPerPage, model: option, key: searchElement, viewType: viewType,
            userTypeIds: [userTypeId]
        });
        dispatch(SimpleSearch({body})).then(
            ({payload: {data: {data, success}}}) => {
                if (success === true) {
                    setSearchResults(data);  // Update the results state if data is successful
                }
            },
        );
    }

    // Use the TableFunction component to handle table logic (checkbox selection, pagination, etc.)
    const {
        t, handleCheckboxChange, handleDeleteAll, pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose
    } = TableFunction({
        selectedList,
        setSelectedList, // Callback to update the selected rows
        data: searchResults, // Table data
        labelRowsPerPage: 'approval_flows_per_page', // Label for rows per page
        hasContainer: true, // Enable container for the table
        fixedPagination: false, // Disable fixed pagination
        setIsAllChecked,
        view: VIEW_TYPE.LIST.name,

        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    return {
        t,
        searchId,
        handleCheckboxChange,
        handleDeleteAll,
        rowsPerPage,
        columns,
        searchResults,
        renderOptionsMenu,
        handleRowExpand,
        RenderDetails,
        handleDelete,
        setSearchResults,
        pagination,
        page
    };
};

export default ApprovalFlowSubViewApp;
